import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import * as Icons from "lucide-react";
import { useContent } from "@/lib/ContentContext";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { ClosingCTA } from "@/pages/Home";

const Icon = ({ name, className = "w-5 h-5" }) => {
  const C = Icons[name] || Icons.Box;
  return <C className={className} aria-hidden="true" />;
};

export default function Channels() {
  const { channels } = useContent();
  const [active, setActive] = useState(channels[2]?.id || "ctv");
  const current = channels.find((c) => c.id === active) || channels[0];

  return (
    <>
      <section className="border-b border-[#D8E2E6]" data-testid="channels-hero">
        <div className="container-riq pt-16 pb-16 lg:pt-24 lg:pb-20">
          <p className="eyebrow mb-5">Channels</p>
          <h1 className="font-display font-black tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            <MaskedLine delay={0.1}>Wherever attention</MaskedLine>
            <MaskedLine delay={0.22}>
              lives, we <span className="accent-word">bid there.</span>
            </MaskedLine>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#4A5A63] leading-relaxed max-w-2xl">
            Display, video, CTV, native, audio, DOOH and mobile — activated from one workflow with unified frequency, pacing and measurement.
          </p>
        </div>
      </section>

      <section className="py-20 lg:py-28" data-testid="channel-explorer">
        <div className="container-riq">
          <div className="flex flex-wrap gap-2" role="tablist" aria-label="Channel selector">
            {channels.map((c) => (
              <button
                key={c.id}
                role="tab"
                aria-selected={active === c.id}
                onClick={() => setActive(c.id)}
                data-testid={`channel-tab-${c.id}`}
                className={`font-mono2 text-xs font-bold uppercase tracking-wider px-4 py-2.5 border border-[#D8E2E6] transition-colors ${
                  active === c.id ? "bg-[#0E7A8F] text-white" : "bg-white hover:bg-[#EDF3F5]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={current.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3 }}
              className="mt-8 card-brut p-6 sm:p-10 grid lg:grid-cols-2 gap-10"
              data-testid="channel-detail"
            >
              <div>
                <div className="flex items-center gap-3">
                  <span className="w-12 h-12 border border-[#D8E2E6] bg-[#E4F3F7] flex items-center justify-center text-[#0E7A8F]">
                    <Icon name={current.icon} className="w-6 h-6" />
                  </span>
                  <h2 className="font-display font-black text-3xl tracking-tight">{current.name}</h2>
                </div>
                <p className="mt-5 text-[#4A5A63] leading-relaxed">{current.note}</p>
                <dl className="mt-6 space-y-3">
                  <div className="border-t border-[#D8E2E6] pt-3">
                    <dt className="font-mono2 text-[10px] uppercase tracking-widest text-[#4A5A63]">Formats</dt>
                    <dd className="font-medium mt-1">{current.formats}</dd>
                  </div>
                  <div className="border-t border-[#D8E2E6] pt-3">
                    <dt className="font-mono2 text-[10px] uppercase tracking-widest text-[#4A5A63]">Supply reach</dt>
                    <dd className="font-medium mt-1">{current.reach}</dd>
                  </div>
                </dl>
              </div>
              <div className="mock-frame self-start" data-testid={`channel-preview-${current.id}`}>
                <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F7F9FA]/20 bg-[#17171B]">
                  <p className="font-mono2 text-[10px] uppercase tracking-widest text-[#F7F9FA]/70">{current.name} · Live bid ticker</p>
                  <span className="font-mono2 text-[10px] font-bold px-2 py-0.5 bg-[#0E7A8F] text-white flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-white tick-dot" aria-hidden="true" /> LIVE
                  </span>
                </div>
                <div className="p-5 space-y-2 font-mono2 text-[11px]">
                  {[
                    ["Clearing CPM", `$${(4 + Math.random() * 20).toFixed(2)}`],
                    ["Win rate (24h)", `${(28 + Math.random() * 20).toFixed(1)}%`],
                    ["Avg. response", `${(9 + Math.random() * 4).toFixed(0)}ms`],
                    ["IVT filtered (pre-bid)", `${(2 + Math.random() * 3).toFixed(1)}%`],
                  ].map(([k, v]) => (
                    <div key={k} className="flex justify-between border-b border-[#F7F9FA]/10 pb-2">
                      <span className="text-[#F7F9FA]/60">{k}</span>
                      <span className="tabular-nums font-bold">{v}</span>
                    </div>
                  ))}
                  <p className="text-[#F7F9FA]/40 pt-1">Simulated ticker — real dashboards show your live data.</p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {channels.slice(0, 7).map((c, i) => (
              <Reveal key={c.id} delay={(i % 4) * 0.06}>
                <button
                  onClick={() => setActive(c.id)}
                  className="card-brut-sm card-hover p-5 text-left w-full"
                  data-testid={`channel-card-${c.id}`}
                >
                  <Icon name={c.icon} className="w-5 h-5 text-[#0E7A8F]" />
                  <h3 className="font-display font-bold text-lg tracking-tight mt-3">{c.name}</h3>
                  <p className="text-xs text-[#4A5A63] mt-1 leading-relaxed">{c.formats}</p>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      <ClosingCTA />
    </>
  );
}
